// src/lib/stabilityLogger.ts
// ---------------------------------------------------------------------------
// Internal-only stability logger.
//
// Surfaces a single, structured console message per critical failure category
// so on-call operators have a clean breadcrumb trail when triaging a
// production incident — without leaking raw stack traces or technical jargon
// to normal users (who only ever see the AppRecovery / RouteErrorBoundary /
// ErrorBoundary fallbacks + toast messages).
//
// Categories covered (matches the production-stability charter §7):
//   • auth-bootstrap          — auth.getSession / profile fetch failed
//   • tree-load               — family tree query failed or timed out
//   • relationship-save       — saving a parent / child / spouse / in-law edge failed
//   • upload-save             — media uploaded to storage but DB row failed to land
//   • invite-acceptance       — invite link / claim / acceptance edge function failed
//
// IMPORTANT:
//   • Never throws. Wraps everything in try/catch.
//   • Never makes network calls.
//   • Never reads PII.
//   • Always uses console.warn / console.error — never alert() / toast().
//   • Also pushes through the existing `errorLogger` ring buffer so the
//     /admin/health page can replay recent failures.
// ---------------------------------------------------------------------------

import { logError } from '@/lib/errorLogger';

export type StabilityCategory =
  | 'auth-bootstrap'
  | 'tree-load'
  | 'relationship-save'
  | 'upload-save'
  | 'invite-acceptance'
  // Upload Reliability Layer V1 — additive categories. Per STABILITY §10
  // these are emitted only by the new queue manager (`src/lib/uploads/`)
  // and never by the locked legacy upload pipelines. Each entry is
  // structured + PII-free.
  | 'upload-start'
  | 'upload-progress'
  | 'upload-timeout'
  | 'upload-retry'
  | 'upload-success'
  | 'upload-failure';

export interface StabilityFailure {
  /** Free-form short label describing the failure (no PII). */
  message: string;
  /** Optional underlying error / payload. Will be stringified safely. */
  cause?: unknown;
  /** Optional contextual ids (NEVER include emails / names / tokens). */
  context?: Record<string, string | number | boolean | null | undefined>;
}

const TAG = '[KinsCircle][Stability]';

function safeStringify(v: unknown): string {
  try {
    if (v == null) return '';
    if (v instanceof Error) {
      return `${v.name}: ${v.message}`;
    }
    if (typeof v === 'string') return v;
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * Log a stability failure. Never user-facing. Returns the same opaque
 * tag string the caller can attach to a toast for support correlation.
 */
export function logStabilityFailure(
  category: StabilityCategory,
  failure: StabilityFailure,
): string {
  const ts = new Date().toISOString();
  const correlation = `${category}-${Date.now().toString(36)}`;

  try {
    // eslint-disable-next-line no-console
    console.error(`${TAG}[${category}]`, {
      ts,
      correlation,
      message: failure.message,
      cause: safeStringify(failure.cause),
      context: failure.context ?? null,
    });
  } catch {
    /* swallow — the logger itself must never crash the app */
  }

  try {
    logError({
      kind: 'other',
      endpoint: `stability:${category}`,
      status: null,
      bodyPreview: safeStringify(failure.cause).slice(0, 240),
      functionName: null,
      attempt: 1,
      message: failure.message,
    });
  } catch {
    /* ignore */
  }

  return correlation;
}

/**
 * Convenience wrappers — one per category, so call sites read like:
 *   logAuthBootstrapFailure({ message: 'profile fetch failed', cause: err });
 */
export const logAuthBootstrapFailure = (f: StabilityFailure) =>
  logStabilityFailure('auth-bootstrap', f);

export const logTreeLoadFailure = (f: StabilityFailure) =>
  logStabilityFailure('tree-load', f);

export const logRelationshipSaveFailure = (f: StabilityFailure) =>
  logStabilityFailure('relationship-save', f);

export const logUploadSaveFailure = (f: StabilityFailure) =>
  logStabilityFailure('upload-save', f);

export const logInviteAcceptanceFailure = (f: StabilityFailure) =>
  logStabilityFailure('invite-acceptance', f);

// ---------------------------------------------------------------------------
// Upload Reliability Layer V1 — additive convenience wrappers (STABILITY §10).
// Emitted only by `src/lib/uploads/uploadQueue.ts` so the new queue produces
// a clean per-upload trail without changing any locked logger behavior.
// ---------------------------------------------------------------------------

export const logUploadStart = (f: StabilityFailure) =>
  logStabilityFailure('upload-start', f);

export const logUploadProgress = (f: StabilityFailure) =>
  logStabilityFailure('upload-progress', f);

export const logUploadTimeout = (f: StabilityFailure) =>
  logStabilityFailure('upload-timeout', f);

export const logUploadRetry = (f: StabilityFailure) =>
  logStabilityFailure('upload-retry', f);

export const logUploadSuccess = (f: StabilityFailure) =>
  logStabilityFailure('upload-success', f);

export const logUploadFailure = (f: StabilityFailure) =>
  logStabilityFailure('upload-failure', f);
